import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { setModal } from 'slices/modalSlice'
import { aplicarFiltrosGenerico } from '../../../helpers/filtros/aplicarFiltrosGenerico'
import { construirArbolEmpresaUsuario } from '../../../helpers/filtros/arbolEmpresaUsuario'
import { setFiltrosSeleccionados, setFiltroLocal } from '../../../slices/filterSlice'
import { DescargarExcel } from '../../pedidos/DescargarExcel'
import { Buscador } from '../buscador/Buscador'
import { Checkbox } from '../checkbox/Checkbox'
import { FechaSelect } from '../fecha/FechaSelect'
import { GenericModal } from '../modal/GenericModal'
import { FiltroEmpresaUsuario } from './FiltroEmpresaUsuario'
import styles from './Filtros.module.css'
import { RadioInput } from './RadioInput'
import { useFiltros } from './useFiltros'

const MAX_OPCIONES_VISIBLES = 5
const CAMPOS_ARBOL_EMPRESA_USUARIO = ['comprador', 'vendedor']
const ITEMS_VACIOS = []

export const Filtros = ({
  mapaFiltros,
  contextoFiltros,
  filtrosVisibles,
  arrayAFiltrar,
  buscador,
  cantidadFiltrosAplicados,
  esAdministrador,
  esVentas
}) => {
  const dispatch = useDispatch()
  const {
    categoriasPath,
    categoriaSeleccionada,
    filtrosCaracteristicas,
    filtrosLocales,
    caracteristicasActuales,
    mapEtiquetaIds
  } = useSelector((state) => state.filters)

  const filtrosSeleccionados = useSelector(
    (state) => state.filters.filtrosSeleccionados,
    shallowEqual
  )
  const modalMetadata = useSelector((state) => state.genericModal.metadata)

  const filtrosAplicados = useMemo(
    () => ({
      ...contextoFiltros,
      ...filtrosLocales,
      categoriaSeleccionada,
      textoBusqueda: buscador?.valor ?? '',
      filtrosSeleccionados,
      caracteristicasActuales,
      mapEtiquetaIds
    }),
    [
      contextoFiltros,
      filtrosLocales,
      categoriaSeleccionada,
      buscador?.valor,
      filtrosSeleccionados,
      caracteristicasActuales,
      mapEtiquetaIds
    ]
  )

  const filtrosParaCategorias = useMemo(
    () => ({
      ...contextoFiltros,
      ...filtrosLocales,
      categoriaSeleccionada,
      textoBusqueda: buscador?.valor ?? ''
    }),
    [contextoFiltros, filtrosLocales, categoriaSeleccionada, buscador?.valor]
  )

  const itemsParaCategorias = useMemo(() => {
    if (!mapaFiltros?.categoriaSeleccionada || !arrayAFiltrar?.length) {
      return ITEMS_VACIOS
    }
    return aplicarFiltrosGenerico(arrayAFiltrar, filtrosParaCategorias, mapaFiltros)
  }, [arrayAFiltrar, filtrosParaCategorias, mapaFiltros])

  const {
    categorias,
    vendedor,
    setVendedor,
    onChangeCategoria,
    categoriaAnterior,
    onResetCategorias,
    limpiarTodosFiltros,
    handleFechaChange,
    cargandoFiltros,
    cargandoCategorias
  } = useFiltros(itemsParaCategorias)

  const filtrosFijos = useMemo(() => {
    if (!mapaFiltros) {
      return []
    }
    return Object.entries(mapaFiltros)
      .filter(([campo, config]) => {
        if (!config.opciones) {
          return false
        }
        if (esAdministrador && CAMPOS_ARBOL_EMPRESA_USUARIO.includes(campo)) {
          return false
        }
        if (filtrosVisibles?.length) {
          return filtrosVisibles.includes(campo)
        }
        return true
      })
      .map(([campo, config]) => {
        const key = typeof config.key === 'function' ? config.key(contextoFiltros) : campo
        const opciones = config.opciones(contextoFiltros)
        const opcionesDisponibles = arrayAFiltrar?.length
          ? opciones.filter((opcion) =>
              aplicarFiltrosGenerico(
                arrayAFiltrar,
                { ...filtrosAplicados, [key]: opcion.valor },
                mapaFiltros
              ).some(() => true)
            )
          : opciones
        return {
          titulo:
            typeof config.titulo === 'function' ? config.titulo(contextoFiltros) : config.titulo,
          tipo: config.tipo,
          value: filtrosLocales[key],
          opciones: opcionesDisponibles,
          onChange: (v) => dispatch(setFiltroLocal({ key, value: v }))
        }
      })
  }, [
    mapaFiltros,
    contextoFiltros,
    filtrosLocales,
    filtrosVisibles,
    esAdministrador,
    dispatch,
    arrayAFiltrar,
    filtrosAplicados
  ])

  const filtrosCaracteristicasDisponibles = useMemo(() => {
    if (!arrayAFiltrar?.length) {
      return filtrosCaracteristicas
    }

    return Object.fromEntries(
      Object.entries(filtrosCaracteristicas)
        .map(([etiqueta, valores]) => [
          etiqueta,
          valores.filter((valor) =>
            aplicarFiltrosGenerico(
              arrayAFiltrar,
              {
                ...filtrosAplicados,
                filtrosSeleccionados: { ...filtrosSeleccionados, [etiqueta]: valor }
              },
              mapaFiltros
            ).some(() => true)
          )
        ])
        .filter(([, valores]) => valores.length > 0)
    )
  }, [arrayAFiltrar, filtrosAplicados, filtrosCaracteristicas, filtrosSeleccionados, mapaFiltros])

  const arbolesEmpresaUsuario = useMemo(() => {
    if (!esAdministrador || !contextoFiltros?.pedidos) {
      return null
    }
    return {
      comprador: construirArbolEmpresaUsuario(contextoFiltros.pedidos, 'comprador'),
      vendedor: construirArbolEmpresaUsuario(contextoFiltros.pedidos, 'vendedor')
    }
  }, [esAdministrador, contextoFiltros])

  const camposArbol = useMemo(
    () =>
      mapaFiltros
        ? Object.entries(mapaFiltros)
            .filter(([, config]) => config.esArbol)
            .map(([campo]) => campo)
        : [],
    [mapaFiltros]
  )

  const checkboxesActivos = useMemo(
    () => filtrosFijos.filter((f) => f.tipo === 'checkbox' && f.value && f.opciones.length !== 0),
    [filtrosFijos]
  )

  const verTodas = (opciones, tipo = 'categoria', etiqueta = null) => {
    dispatch(
      setModal({
        size: 'lg',
        title:
          tipo === 'categoria' ? 'Selecciona una categoría' : `Filtrar por ${etiqueta || tipo}`,
        type: '',
        show: true,
        metadata: { tipo, etiqueta }
      })
    )
    dispatch(setModal({ items: opciones }))
  }

  const ordenarOpciones = (opciones, value) => {
    if (value === null || value === '') {
      return opciones
    }

    return [...opciones].sort((a, b) => {
      if (String(a.valor) === String(value)) {
        return -1
      }
      if (String(b.valor) === String(value)) {
        return 1
      }
      return 0
    })
  }

  const handlePrecioChange = (value, tipo) => {
    if (value === '' || Number(value) >= 0) {
      dispatch(setFiltroLocal({ key: tipo === 'desde' ? 'precioDesde' : 'precioHasta', value }))
    }
  }

  const handleKeyDownPrecio = (e) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault()
    }
  }

  const entradasFecha = useMemo(
    () =>
      mapaFiltros
        ? Object.entries(mapaFiltros)
            .filter(([, c]) => c.tipo === 'fecha')
            .map(([key, c]) => ({ key, label: c.label }))
        : [],
    [mapaFiltros]
  )

  const mostrarPrecio = useMemo(
    () =>
      mapaFiltros && filtrosVisibles.some((f) => f === 'precio')
        ? Object.values(mapaFiltros).some((c) => c.tipo === 'precio')
        : false,
    [mapaFiltros, filtrosVisibles]
  )

  const seleccionarOpcionModal = (item) => {
    const tipo = modalMetadata?.tipo || 'categoria'
    if (tipo === 'filtroFijo') {
      const filtroSeleccionado = filtrosFijos.find(
        (filtro) => filtro.titulo === modalMetadata?.etiqueta
      )
      if (filtroSeleccionado?.onChange) {
        filtroSeleccionado.onChange(item.valor)
      }
    } else if (camposArbol.includes(tipo)) {
      dispatch(setFiltroLocal({ key: tipo, value: item.valor }))
      if (tipo === 'compradorEmpresa') {
        dispatch(setFiltroLocal({ key: 'compradorUsuario', value: '' }))
      } else if (tipo === 'vendedorEmpresa') {
        dispatch(setFiltroLocal({ key: 'vendedorUsuario', value: '' }))
      }
    } else if (tipo === 'categoria') {
      onChangeCategoria(item.value, item.label)
    } else if (tipo === 'caracteristica') {
      const etiqueta = modalMetadata?.etiqueta
      if (etiqueta) {
        dispatch(setFiltrosSeleccionados({ ...filtrosSeleccionados, [etiqueta]: item.value }))
      }
    }
  }

  const limpiarFiltros = () => {
    limpiarTodosFiltros()

    if (buscador?.setBusqueda) {
      buscador.setBusqueda('')
    }
    if (buscador?.onChange) {
      buscador.onChange('')
    }
  }

  return (
    <div className={styles.filtros}>
      {cargandoFiltros || cargandoCategorias ? (
        <div className="d-flex justify-content-center p-4">
          <Spinner animation="border" role="status" aria-label="Cargando" />
        </div>
      ) : (
        <>
          <GenericModal onSelect={seleccionarOpcionModal} />
          {buscador && (
            <Buscador
              onChange={buscador.onChange}
              placeholder={buscador.placeholder}
              busqueda={buscador.valor}
              setBusqueda={buscador.setBusqueda}
            />
          )}
          {cantidadFiltrosAplicados > 0 && (
            <div className={styles.limpiarFiltrosContainer}>
              <button className={styles.btnLimpiarFiltros} onClick={limpiarFiltros}>
                Limpiar filtros
              </button>
            </div>
          )}
          {(categoriasPath.length > 0 ||
            (vendedor && vendedor.id !== 0) ||
            checkboxesActivos.length > 0) && (
            <div className={styles.filtrosActivos}>
              {categoriasPath.length > 0 && (
                <div className={styles.pathContainer}>
                  {categoriasPath.map((cat, index) => (
                    <div key={cat.id}>
                      <span
                        className={styles.categoriaPath}
                        onClick={() => categoriaAnterior(cat.id)}
                      >
                        {cat.label}
                      </span>
                      {index < categoriasPath.length - 1 && ' > '}
                    </div>
                  ))}
                  <span className={styles.clearPath} onClick={onResetCategorias}>
                    <i className="bi bi-x"></i>
                  </span>
                </div>
              )}
              {vendedor && vendedor.id !== 0 && (
                <div className={styles.pathContainer}>
                  <span className={styles.categoriaPath}>{vendedor.label}</span>
                  <span
                    className={styles.clearPath}
                    onClick={() => setVendedor({ id: 0, label: '' })}
                  >
                    <i className="bi bi-x"></i>
                  </span>
                </div>
              )}
              {checkboxesActivos.map(({ titulo, opciones, onChange }) => (
                <div className={styles.pathContainer} key={titulo}>
                  <span className={styles.categoriaPath}>
                    {titulo}: {opciones[0].label}
                  </span>
                  <span className={styles.clearPath} onClick={() => onChange('')}>
                    <i className="bi bi-x"></i>
                  </span>
                </div>
              ))}
            </div>
          )}
          {categorias.length > 0 && (
            <div className={styles.filtroItem}>
              <h3>Categoría:</h3>
              {categorias.slice(0, MAX_OPCIONES_VISIBLES).map((cat) => (
                <span
                  key={cat.value}
                  className={styles.crumb}
                  onClick={() => onChangeCategoria(cat.value, cat.label)}
                >
                  {cat.label}
                </span>
              ))}
              {categorias.length > MAX_OPCIONES_VISIBLES && (
                <span className={styles.verMas} onClick={() => verTodas(categorias, 'categoria')}>
                  Ver Todas
                </span>
              )}
            </div>
          )}
          {arbolesEmpresaUsuario &&
            (!filtrosVisibles?.length || filtrosVisibles.includes('comprador')) && (
              <FiltroEmpresaUsuario
                campo="comprador"
                titulo="Comprador"
                arbol={arbolesEmpresaUsuario.comprador}
                empresaSeleccionada={filtrosLocales.compradorEmpresa}
                usuarioSeleccionado={filtrosLocales.compradorUsuario}
                onChangeEmpresa={(v) =>
                  dispatch(setFiltroLocal({ key: 'compradorEmpresa', value: v }))
                }
                onChangeUsuario={(v) =>
                  dispatch(setFiltroLocal({ key: 'compradorUsuario', value: v }))
                }
                onVerTodas={verTodas}
              />
            )}
          {arbolesEmpresaUsuario &&
            (!filtrosVisibles?.length || filtrosVisibles.includes('vendedor')) && (
              <FiltroEmpresaUsuario
                campo="vendedor"
                titulo="Vendedor"
                arbol={arbolesEmpresaUsuario.vendedor}
                empresaSeleccionada={filtrosLocales.vendedorEmpresa}
                usuarioSeleccionado={filtrosLocales.vendedorUsuario}
                onChangeEmpresa={(v) =>
                  dispatch(setFiltroLocal({ key: 'vendedorEmpresa', value: v }))
                }
                onChangeUsuario={(v) =>
                  dispatch(setFiltroLocal({ key: 'vendedorUsuario', value: v }))
                }
                onVerTodas={verTodas}
              />
            )}
          {filtrosFijos.map(
            ({ titulo, tipo, value, opciones, onChange }) =>
              opciones.length !== 0 &&
              (tipo === 'checkbox' ? (
                <div key={titulo} className={styles.filtroItem}>
                  <h3>{titulo}:</h3>
                  <Checkbox
                    type="checkbox"
                    className={styles.checkFiltro}
                    id={`filtro-${titulo.replace(/\s+/g, '-')}`}
                    name={titulo}
                    label={opciones[0].label}
                    checked={Boolean(value)}
                    onChange={() => onChange(value ? false : opciones[0].valor)}
                  />
                </div>
              ) : (
                <div key={titulo} className={styles.filtroItem}>
                  <h3>{titulo}:</h3>
                  {ordenarOpciones(opciones, value)
                    .slice(0, MAX_OPCIONES_VISIBLES)
                    .map(({ label, valor }) => (
                      <RadioInput
                        key={valor}
                        valor={label}
                        isSelected={String(value) === String(valor)}
                        onClick={(l) => {
                          if (l === label) onChange(valor)
                          else onChange('')
                        }}
                      />
                    ))}
                  {opciones.length > MAX_OPCIONES_VISIBLES && (
                    <span
                      className={styles.verMas}
                      onClick={() => verTodas(opciones, 'filtroFijo', titulo)}
                    >
                      Ver Todas
                    </span>
                  )}
                </div>
              ))
          )}
          {mostrarPrecio && (
            <div className={styles.filtroItem}>
              <h3>Precio:</h3>
              <div className={styles.precioInputs}>
                <input
                  type="number"
                  placeholder="Desde"
                  value={filtrosLocales.precioDesde ?? ''}
                  onChange={(e) => handlePrecioChange(e.target.value, 'desde')}
                  onKeyDown={handleKeyDownPrecio}
                  className={styles.precioInput}
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Hasta"
                  value={filtrosLocales.precioHasta ?? ''}
                  onChange={(e) => handlePrecioChange(e.target.value, 'hasta')}
                  onKeyDown={handleKeyDownPrecio}
                  className={styles.precioInput}
                  min="0"
                />
              </div>
            </div>
          )}
          {Object.entries(filtrosCaracteristicasDisponibles).map(([etiqueta, valores]) => {
            const selectedValue = filtrosSeleccionados[etiqueta]
            const valoresOrdenados = selectedValue
              ? [...valores].sort((a, b) => {
                  if (a === selectedValue) return -1
                  if (b === selectedValue) return 1
                  return 0
                })
              : valores
            return (
              <div className={styles.filtroItem} key={etiqueta}>
                <h3>{etiqueta}:</h3>
                {valoresOrdenados.slice(0, MAX_OPCIONES_VISIBLES).map((valor) => {
                  const isSelected = filtrosSeleccionados[etiqueta] === valor
                  return (
                    <RadioInput
                      key={valor}
                      onClick={(v) => {
                        dispatch(
                          setFiltrosSeleccionados({ ...filtrosSeleccionados, [etiqueta]: v })
                        )
                      }}
                      valor={valor}
                      isSelected={isSelected}
                    />
                  )
                })}
                {valores.length > MAX_OPCIONES_VISIBLES && (
                  <span
                    className={styles.verMas}
                    onClick={() =>
                      verTodas(
                        valores.map((v) => ({ value: v, label: v })),
                        'caracteristica',
                        etiqueta
                      )
                    }
                  >
                    Ver Todas
                  </span>
                )}
              </div>
            )
          })}
          {entradasFecha.length > 0 && (
            <div className={styles.filtroItem}>
              {entradasFecha.map((entrada) => (
                <FechaSelect
                  key={entrada.key}
                  fecha={filtrosLocales[entrada.key] ?? null}
                  guardarFecha={handleFechaChange(
                    entrada.key,
                    entrada.key.includes('Desde')
                      ? entrada.key.replace('Desde', 'Hasta')
                      : entrada.key.replace('Hasta', 'Desde')
                  )}
                  label={entrada.label}
                  isSelected={Boolean(filtrosLocales[entrada.key])}
                  deshabilitaFechaFutura={new Date().toISOString().split('T')[0]}
                />
              ))}
            </div>
          )}
        </>
      )}
      {entradasFecha && entradasFecha.length > 0 && (esVentas || esAdministrador) && (
        <DescargarExcel
          fechaDesde={filtrosLocales[entradasFecha[0].key]}
          fechaHasta={filtrosLocales[entradasFecha[1].key]}
          filtrosFijos={filtrosFijos}
          esAdministrador={esAdministrador}
          esVentas={esVentas}
        />
      )}
    </div>
  )
}

Filtros.propTypes = {
  mapaFiltros: PropTypes.object,
  contextoFiltros: PropTypes.object,
  arrayAFiltrar: PropTypes.arrayOf(PropTypes.object),
  filtrosVisibles: PropTypes.arrayOf(PropTypes.string),
  buscador: PropTypes.shape({
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    valor: PropTypes.string,
    setBusqueda: PropTypes.func
  }),
  limpiarFiltros: PropTypes.func,
  cantidadFiltrosAplicados: PropTypes.number,
  esAdministrador: PropTypes.bool,
  esVentas: PropTypes.bool
}
